<template>
  <Directory
    class="companies"
    v-bind="{
      activeFilters,
      inputText: filterValues.input_text,
      isLoading,
      results,
      resultCount,
      unitLabels,
    }"
    @text-input="filterValues.input_text = $event"
    @clear-all="clearAllFilters"
    @clear-filter="clearFilter"
    @more-click="fetchNextPage"
  >
    <div class="companies__filters-list" slot="filters">
      <div class="companies__filters-line-1">
        <CmSelect
          v-for="filter in filters"
          class="companies__filter companies__filter--select"
          :key="filter.id"
          :name="filter.id"
          :options="filter.options"
          :null-label="filter.nullLabel"
          :label="filter.label"
          :multiple="filter.multiple"
          :include-search="filter.options.length > 12"
          :value="filterValues[filter.id]"
          @input="filterValues[filter.id] = $event"
          compact
        />
        <LocationFilter
          class="companies__filter companies__filter--select"
          v-model="filterValues.location"
          :location="filterValues.location"
          :radius="filterValues.radius"
          @location="filterValues.location = $event"
          @coords="filterValues.coords = $event"
          @radius="filterValues.radius = $event"
        />
        <Checkbox
          class="companies__filter companies__filter--checkbox"
          v-model="filterValues.is_hiring"
          label="Hiring!"
        />
        <Checkbox
          class="companies__filter companies__filter--checkbox"
          v-model="filterValues.is_favorite"
          label="My Favorites"
        />
      </div>
    </div>

    <div class="companies__list">
      <CompanyCard
        class="companies__job"
        v-for="result in results"
        :liked="dynamicFavorites.includes(result.id)"
        :key="result.id"
        :company="result"
        :is-logged-in="isLoggedIn"
        @like="likeCompany"
        @unlike="unlikeCompany"
      />
    </div>
  </Directory>
</template>

<script>
// If there's a variable in the template or in the JS that you can't find,
// CHECK THE MIXIN

import CompanyCard from "../components/company-card";
import DirectoryMixin from "../mixins/directory";

import { preprocessQueryString } from "../lib/elastic-search";
import { getURLParams } from "../lib/url";

export default {
  mixins: [DirectoryMixin],

  components: {
    CompanyCard
  },

  props: {
    isLoggedIn: {
      type: Boolean
    },
    favorites: {
      type: Array
    }
  },

  data() {
    let filters = this.generateFilters();

    return {
      filterArray: filters,
      defaultValues: this.generateDefaultFilterValues(filters),
      filterValues: this.generateFilterValues(filters),
      unitLabels: {
        singular: "Company",
        plural: "Companies"
      },
      index: "companies",

      dynamicFavorites: this.favorites
    };
  },

  computed: {
    filterKeys() {
      return [
        "skill_tag_id",
        "industry_id",
        "size",
        "is_hiring",
        "is_favorite",
        "location",
        "coords"
      ];
    },

    activeFilters() {
      const values = this.filterValues;
      const defaults = this.defaultValues;

      const sizeFilter = this.filters.find(
        propsFilter => propsFilter.id === "size"
      );

      return [
        {
          label:
            (sizeFilter.options.find(option => option.value === values.size)
              ? sizeFilter.options.find(option => option.value === values.size)
                  .label
              : sizeFilter.nullLabel) +
            (values.size === "1..1" ? " Person" : " People"),
          keys: ["size"],
          value: values.size
        },
        {
          label: "Hiring!",
          keys: ["is_hiring"],
          value: values.is_hiring
        },
        {
          label: "My Favorites",
          keys: ["is_favorite"],
          value: values.is_favorite
        },
        ...values.skill_tag_id.map(filterValue => ({
          keys: ["skill_tag_id"],
          match: filterValue,
          value: filterValue,
          label: this.filters
            .find(propsFilter => propsFilter.id === "skill_tag_id")
            .options.find(option => option.value === filterValue).label
        })),
        ...values.industry_id.map(filterValue => ({
          keys: ["industry_id"],
          match: filterValue,
          value: filterValue,
          label: this.filters
            .find(propsFilter => propsFilter.id === "industry_id")
            .options.find(option => option.value === filterValue).label
        })),
        values.coords.lat &&
          values.coords.lng && {
            keys: ["location", "coords"],
            label: `Within ${values.radius} of ${values.location}`
          }
      ]
        .filter(Boolean)
        .filter(filter => filter.value !== defaults[filter.keys[0]]);
    },

    results() {
      if (this.response) {
        return this.response.hits.hits.map(el => {
          return {
            ...el._source
          };
        });
      } else return [];
    },

    sizeFilterValue() {
      if (this.filterValues.size) {
        const rangeArray = this.filterValues.size.split("..");
        return { min: rangeArray[0], max: rangeArray[1] };
      } else {
        return this.filterValues.size;
      }
    },

    query() {
      const filters = this.filterValues;
      return {
        query: {
          bool: {
            must: [
              { match: { application_status: "published" } },
              this.sizeFilterValue && {
                range: {
                  size: {
                    gte: this.sizeFilterValue.min,
                    lte: this.sizeFilterValue.max
                  }
                }
              },
              filters.skill_tag_id.length && {
                terms: {
                  "skill_tags.id": filters.skill_tag_id
                }
              },
              filters.industry_id.length && {
                terms: {
                  "industries.id": filters.industry_id
                }
              },
              filters.is_favorite && {
                terms: {
                  id: this.dynamicFavorites
                }
              },
              filters.remoteFriendly && {
                bool: {
                  should: [
                    { match: { remote: true } },
                    { match: { remote_friendly: true } }
                  ]
                }
              },
              filters.is_hiring && {
                match: {
                  is_hiring: true
                }
              },
              filters.coords.lat &&
                filters.coords.lng && {
                  geo_distance: {
                    distance: filters.radius,
                    coordinates: `${filters.coords.lat},${filters.coords.lng}`
                  }
                },
              filters.input_text !== "" && {
                query_string: {
                  query: preprocessQueryString(filters.input_text),
                  default_operator: "AND",
                  fuzziness: 1,
                  fuzzy_prefix_length: 0,
                  fuzzy_max_expansions: 10
                }
              }
            ].filter(Boolean)
          }
        }
      };
    }
  },

  methods: {
    generateFilters() {
      return [
        {
          key: "input_text",
          initial: getURLParams().get("input_text"),
          default: ""
        },
        {
          key: "skill_tag_id",
          urlKey: "skill_id",
          initial: getURLParams().getAll("skill_id"),
          default: []
        },
        {
          key: "industry_id",
          initial: getURLParams().getAll("industry_id"),
          default: []
        },
        {
          key: "size",
          initial: getURLParams().get("size"),
          default: null
        },
        {
          key: "is_hiring",
          initial: getURLParams().get("is_hiring"),
          default: false
        },
        {
          key: "is_favorite",
          initial: getURLParams().get("is_favorite"),
          default: false
        },
        {
          key: "radius",
          initial: getURLParams().get("radius"),
          default: "5 miles"
        },
        {
          key: "coords",
          initial: JSON.parse(getURLParams().get("coords")),
          default: false
        },
        {
          key: "location",
          initial: getURLParams().get("location"),
          default: ""
        }
      ];
    },

    likeCompany(id) {
      this.dynamicFavorites.push(id);
    },

    unlikeCompany(id) {
      this.dynamicFavorites = this.dynamicFavorites.filter(fave => fave !== id);
    }
  }
};
</script>

<style lang="scss" scoped>
.companies {
  position: relative;

  &__filters-line-1,
  &__filters-line-2 {
    display: flex;
    margin: 0 0 12px;
    align-items: baseline;
    @media (max-width: 800px) {
      flex-direction: column;
    }
  }
  &__filters-line-2 {
    justify-content: flex-end;
  }
  &__filter {
    flex: 1 1 auto;
    &--select {
      @media (max-width: 800px) {
        width: 100%;
      }
    }
    &--checkbox {
      flex: 0 0 auto;
    }
    & + & {
      margin-left: 12px;
      @media (max-width: 800px) {
        margin-left: 0;
        margin-top: 12px;
      }
    }
  }

  &__list {
    grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
    display: grid;
    grid-gap: 2rem;
    padding-top: 2rem;
  }
}
</style>



// WEBPACK FOOTER //
// app/javascript/pages/companies.vue?ef5f5100